import Component from '@glimmer/component'
import { t } from 'ember-intl'
import RouteTemplate from 'ember-route-template'
import PageHeader from 'teacher-ui-client/components/page-header/index.gts'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { service } from '@ember/service'
import { EWICatalogueList } from 'teacher-ui-client/components/writing-legends/ewi-catalogue-list.gts'
import InputText from 'teacher-ui-client/components/base/input-text.gts'
import { tracked } from '@glimmer/tracking'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import pick from 'teacher-ui-client/helpers/pick'
import { on } from '@ember/modifier'
import RadioButtonGroup from 'teacher-ui-client/components/base/radio-button-group.gts'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import GradeSelect from 'teacher-ui-client/components/base/select/grade-position/index.gts'
import { array } from '@ember/helper'
import type ExcitingWritingIdeasController from 'teacher-ui-client/subscription-type/writing-ideas/index/controller'
import BaseSelect from 'teacher-ui-client/components/base/select/index.gts'

export interface EWIFilterButton {
  value: string
  text: string
}

interface Signature {
  Args: {
    controller: ExcitingWritingIdeasController
  }
}

const secondaryTypes: string[] = [
  'Advertisements',
  'Biographies & Autobiographies',
  'Descriptive Writing',
  'Diary and Blog Writing',
  'Emails',
  'Instructions and Procedures',
  'Interviews',
  'Letter Writing and Invitations',
  'Narratives',
  'Newspaper Reports',
  'Recounts',
  'Reviews',
  'Scriptwriting',
  'Sentence Building',
]
export class ExcitingWritingIdeasPage extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService

  @service gradeSets!: GradeSetsService

  @tracked textFilter: Nullable<string> = null

  get textTypeFilterButtons(): EWIFilterButton[] {
    return [
      { value: 'All', text: 'All' },
      { value: 'Imaginative', text: 'Imaginative' },
      { value: 'Informative', text: 'Informative' },
      { value: 'Persuasive', text: 'Persuasive' },
    ]
  }

  get gradePositions() {
    return this.gradeSets.grades.map((grade) => grade.position)
  }

  updateGradeFilter = (gradePosition: number) => {
    this.args.controller.grade = gradePosition
  }

  filterByTextType = (value: string) => {
    this.args.controller.tags = value
  }

  updateTextFilter = (value: string) => {
    this.textFilter = value
    this.args.controller.pageNumber = 1 // Reset to first page when text filter changes
  }

  get gradeOptions() {
    return this.gradeSets.optionsBy('name')
  }

  <template>
    <div class="grow">
      <PageHeader>
        <:title>
          <h1>
            {{t "pages.excitingWritingIdeas.title"}}
          </h1>
        </:title>
      </PageHeader>
      <div class="flex justify-between">
        <GradeSelect
          data-test-grade-select
          class="flex"
          @additionalOptions={{array AllGradesOption}}
          @gradePositions={{this.gradePositions}}
          @gradePosition={{@controller.grade}}
          @updateGradePosition={{this.updateGradeFilter}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />
        <InputText
          data-test-ewi-search
          type="search"
          role="searchbox"
          name="search-filter"
          class="w-72"
          value={{this.textFilter}}
          @themeKey={{themeKey this.subscriptionType.current}}
          placeholder={{t "pages.excitingWritingIdeas.filters.searchPlaceholder"}}
          aria-label={{t "pages.excitingWritingIdeas.filters.searchPlaceholder"}}
          @icon="search"
          @iconEnd={{true}}
          {{on "input" (pick "target.value" this.updateTextFilter)}}
        />
      </div>
      <div class="m-4 flex flex-wrap items-center justify-center gap-4">
        <RadioButtonGroup
          data-test-text-type-radio
          class="flex"
          @name="text-type-select"
          @buttons={{this.textTypeFilterButtons}}
          @value={{@controller.tags}}
          @onSelect={{this.filterByTextType}}
        />
        <BaseSelect
          data-test-text-type-select
          @themeKey={{themeKey this.subscriptionType.current}}
          id="text-type-select"
          name="text-type-select"
          value={{@controller.tags}}
          {{on "change" (pick "target.value" this.filterByTextType)}}
        >
          <option value="" disabled selected hidden>
            {{t "pages.excitingWritingIdeas.filters.selectTextType"}}
          </option>
          {{#each secondaryTypes as |type|}}
            <option value={{type}}>
              {{type}}
            </option>
          {{/each}}
        </BaseSelect>
      </div>
      <div class="flex w-full justify-center">
        <div class="mx-auto mt-10">
          <EWICatalogueList
            @tagFilter={{@controller.tags}}
            @textFilter={{this.textFilter}}
            @pageNumber={{@controller.pageNumber}}
            @perPage={{@controller.perPage}}
            @grade={{@controller.grade}}
          />
        </div>
      </div>
    </div>
  </template>
}

export default RouteTemplate(ExcitingWritingIdeasPage)
